import { isValid } from 'date-fns';
import { Material, ServiceLine, ServiceLineMaterial } from 'types/models';
import { CurrentSampleOrder, ExtendedSampleResultsDTO, Nullable } from '@cast/leadcast-types';
import { v4 as uuidv4 } from 'uuid';
import { FileLocationType, SAMPLE_ANALYTE_CONSTANTS } from './constants';
import { CASTFile } from 'types/models/CASTFile';

export const getNestedValue = (data, key) => {
  if (!data || !key) return null;

  let keySplit = key.split('.');
  let value = data;

  for (let i = 0; i < keySplit.length; i++) {
    if (value === null) break;
    let subKey = keySplit[i];
    value = value[subKey];
  }

  return value;
};

export function downloadFileFromURL(url: string, fileName: string) {
  const link = document.createElement('a');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.href = url;
  link.download = fileName;
  link.click();
}

export function downloadBlob(blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  downloadFileFromURL(url, fileName);
  window.URL.revokeObjectURL(url);
}

export function downloadCASTFile(file: CASTFile) {
  downloadFileFromURL(file.url, file.fileName);
}

export function isJSON(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export function parseIfJSON(text: string) {
  if (isJSON(text)) {
    return JSON.parse(text);
  }
  return null;
}

// TODO: There is a similar function in features/customerportal
export function buildAddressStringFromRecord(a: {
  address: string;
  addressUnit?: string;
  city: string;
  state: string;
  zipcode: string;
}): string {
  if (!a) return '';

  const { address, addressUnit, city, state, zipcode } = a;

  if (!address || !city || !state || !zipcode) {
    return '';
  }

  const fullAddress = addressUnit ? `${address} ${addressUnit}` : address;

  const addressParts: string[] = [];

  addressParts.push(fullAddress.trim());
  addressParts.push(city.trim());
  addressParts.push(`${state.trim()} ${zipcode.trim()}`);

  return addressParts.join(', ');
}

export function round(decimal: Nullable<number>, roundTo: number) {
  if (!decimal) return decimal;
  const base = Math.pow(10, roundTo);
  return Math.round((decimal + Number.EPSILON) * base) / base;
}

export function formatNumber(num?: number): string {
  if (num === 0) return '0';
  if (!num) return '';
  return num.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
}

type Arg<T> = T | Promise<T>;
export async function hash<T>(
  object: { [P in keyof T]: Arg<T[P]> },
  allSettled?: boolean
): Promise<T> {
  const keys: string[] = [];
  const promises = Object.keys(object).map((k) => {
    keys.push(k);
    return object[k];
  });
  let resolutions = allSettled ? await Promise.allSettled(promises) : await Promise.all(promises);

  if (allSettled) {
    resolutions = resolutions.map((r) => {
      if (r.status === 'fulfilled') {
        return r.value;
      }
      return undefined;
    });
  }
  const result = {} as Promise<T>;
  keys.forEach((key, index) => (result[key] = resolutions[index]));
  return result;
}

export function sliceIntoChunks<T extends any[]>(arr: T, chunkSize: number): T[] {
  const res = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
}

export function keysOf<T extends Object>(obj: T): Array<keyof T> {
  return Array.from(Object.keys(obj)) as any;
}

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

export function entriesOf<T>(obj: T): Entries<T> {
  return Object.entries(obj) as any;
}

// build a date string (1/1/1970) from a Date() argument
export function getDateString(input: string | Date): string {
  const dateObject = new Date(input);
  const month = dateObject.getUTCMonth() + 1;
  const day = dateObject.getUTCDate();
  const year = dateObject.getUTCFullYear();

  return `${month}/${day}/${year}`;
}

const materialRank = {
  LD: 1,
  GALV: 2,
  UNK: 4,
};

export function byWorstMaterial(
  slm1: ServiceLineMaterial | Material,
  slm2: ServiceLineMaterial | Material
) {
  let matCode1;
  let matCode2;
  if (typeof slm1.material === 'string' && typeof slm2.material === 'string') {
    matCode1 = slm1.material;
    matCode2 = slm2.material;
  } else if (typeof slm1.material === 'object' && typeof slm2.material === 'object') {
    matCode1 = slm1.material.code;
    matCode2 = slm2.material.code;
  }
  const slm1Rank = materialRank[matCode1] ?? 3;
  const slm2Rank = materialRank[matCode2] ?? 3;
  return slm1Rank - slm2Rank;
}

export function byWorstConnectorMaterial(sl1: ServiceLine, sl2: ServiceLine) {
  const slm1Rank = materialRank[sl1.connectorMaterial?.code] ?? 3;
  const slm2Rank = materialRank[sl2.connectorMaterial?.code] ?? 3;
  return slm1Rank - slm2Rank;
}

export function onlyUnique(value: string | number, index: number, array: any[]) {
  return array.indexOf(value) === index;
}

export function isValidHex(hex: string) {
  return /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);
}

export function buildHalfCircleIcon(colorLeft: string, colorRight?: string) {
  return `
    <svg width='20' height='20' transform='rotate(-90)' xmlns='http://www.w3.org/2000/svg'>
      <circle cx='10' cy='10' r='10' fill='${colorLeft}' />
      <path d='M0,10 a1,1 0 0,0 20,0' fill='${colorRight ?? colorLeft}' />
    </svg>
  `;
}

export const parseFromValuesOrFunc = <T, U>(
  val: ((arg: U) => T) | T | undefined,
  arg?: U
): T | undefined => (val instanceof Function ? val(arg) : val);

export function validDateOrEmptyString(date: string, formatFunc: (date: Date) => string) {
  if (!date) return '';
  const dateObj = new Date(date);
  if (!isValid(dateObj)) return '';
  return formatFunc(dateObj);
}

export const getMaterialDescriptionFromCode = (code: string) => {
  switch (code?.toUpperCase()) {
    case 'ST':
      return 'Steel';
    case 'AC':
      return 'Asbestos Cement';
    case 'BR':
      return 'Brass';
    case 'CI':
      return 'Cast Iron';
    case 'CU':
      return 'Copper';
    case 'DI':
      return 'Ductile Iron';
    case 'GALV':
      return 'Galvanized Steel';
    case 'PE':
      return 'Polyethylene';
    case 'PP':
      return 'Polypropylene';
    case 'PVC':
      return 'Polyvinyl Chloride';
    case 'TTE':
      return 'Transite';
    case 'WD':
      return 'Wood';
    case 'HDPE':
      return 'High Density Polyethylene';
    case 'FRP':
      return 'Fiberglass Reinforced Pipe';
    case 'PL':
      return 'Plastic';
    case 'UNK':
      return 'Unknown';
    case 'UNK-NL':
      return 'Unknown - Not Lead';
    case 'LD':
      return 'Lead';
    case 'BI':
      return 'Black Iron';
    case 'WI':
      return 'Wrought Iron';
    case 'TB':
      return 'Tubeuloy';
    case 'OTH':
      return 'Other Metallic';
    default:
      return code;
  }
};
export function generateBucketLocationString(
  tenantName: string,
  fileLocation: FileLocationType,
  fileName: string
) {
  return `${tenantName}/${fileLocation}/${uuidv4()}/${fileName}`;
}

export function getPublicTenantName() {
  // For some reason, session storage is getting cleared out (?) on certain browsers
  // If tenant name was cleared out of session storage, use tenantName from url query string
  const tenantNameInSessionStorage = sessionStorage.getItem('tenantName');
  if (!!tenantNameInSessionStorage) {
    return tenantNameInSessionStorage;
  } else {
    const params = new URL(document.location.toString()).searchParams;
    return params.get('tenantName');
  }
}

export function inspectionIsPending(inspectionStatus) {
  return (
    inspectionStatus === 'Pending Approval' ||
    inspectionStatus === 'Pending Approval (Unverified Customer)'
  );
}

export function wait(milleseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milleseconds));
}

export function generateAlphaNumericString(length: number) {
  function randomString(l) {
    const chars = '23456789abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ';
    var result = '';
    for (var i = l; i > 0; --i) result += chars[Math.round(Math.random() * (chars.length - 1))];
    return result;
  }

  return randomString(length);
}

export function capitalizeFirstLetter(str: string) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const formatToKebabCase = (text: string): string => {
  if (!text) return '';

  // Check if text is a string with toLowerCase method
  const stringText = typeof text === 'string' ? text : String(text);

  return stringText
    .toLowerCase()
    .trim()
    .replace(/,/g, '') // Remove all commas
    .replace(/[\s&]+/g, '-') // Replace spaces and ampersands with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

export function resultIsLead(result: ExtendedSampleResultsDTO): boolean {
  return result.analyte?.analyteName?.toLowerCase() === SAMPLE_ANALYTE_CONSTANTS.LEAD.toLowerCase();
}
